/**
 * Single source of truth for the set of legal page segments rendered by the
 * legal shell. Adding a new segment requires:
 *   1. Append to LEGAL_SEGMENTS
 *   2. Add a `nav.<key>` translation in messages/legal-{locale}.json
 *   3. Add a section component in `sections/` and wire it in `renderLegalSection`
 *   4. Add per-app routes (apps/web `[locale]/[country]/legal/(shell)/<seg>/page.tsx`,
 *      apps/app `_anon/legal/<seg>.tsx` or via dynamic `$segment`)
 */
export const LEGAL_SEGMENTS = [
  "terms",
  "privacy",
  "cookies",
  "gdpr",
  "security",
  "ehds",
  "data-protection",
  "data-deletion",
  "listed-professionals",
  "accessibility",
  "dsa",
  "partnership",
] as const;

export type LegalSegment = (typeof LEGAL_SEGMENTS)[number];

/**
 * Maps URL segment ↔ i18n nav-label key. URL slugs use kebab-case
 * (`data-protection`); JSON keys are camelCase (`dataProtection`) for
 * consistency with the rest of the catalog.
 */
export const LEGAL_NAV_KEYS: Record<LegalSegment, string> = {
  terms: "terms",
  privacy: "privacy",
  cookies: "cookies",
  gdpr: "gdpr",
  security: "security",
  ehds: "ehds",
  "data-protection": "dataProtection",
  "data-deletion": "dataDeletion",
  "listed-professionals": "listedProfessionals",
  accessibility: "accessibility",
  dsa: "dsa",
  partnership: "partnership",
};

export function isLegalSegment(value: string | undefined | null): value is LegalSegment {
  return !!value && (LEGAL_SEGMENTS as readonly string[]).includes(value);
}

/**
 * Date shown in the "last updated" footer of every legal section.
 * Single source of truth — bump whenever copy in messages/legal-*.json changes.
 */
export const LEGAL_LAST_UPDATED = "2026-10-22";
